import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { InsuranceData } from "../../interfaces/DTO/insurance_data/InsuranceData";
import { ManualMimshakData, ManualProductType } from "../../interfaces/DTO/manualMimshakData";
import { ReportType, ReportOrigin } from "../../interfaces/DTO/mimshakData";
import { ManualAssetForm } from "../ManualMimshak/ManualAssetForm";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { formatCurrency } from "../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";

interface Props {
  open: boolean;
  onClose: () => void;
  insurancePolicies: InsuranceData[];
  contacts: Contact[];
  reportedPeriod: string;
  onPolicySaved?: (policy: InsuranceData) => void;
}

export const InsuranceToManualMimshakConverter: React.FC<Props> = ({
  open,
  onClose,
  insurancePolicies,
  contacts,
  reportedPeriod,
  onPolicySaved,
}) => {
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const getContactById = (contactId: number): Contact | undefined => {
    return contacts.find((c) => c.id === contactId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl" sx={{ zIndex: 1100 }}>
      <DialogTitle>
        <Typography variant="h5">המרת פוליסות חיסכון לנכסים ידניים</Typography>
      </DialogTitle>

      <DialogContent sx={{ direction: "rtl" }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          נמצאו {insurancePolicies.length} פוליסות חיסכון שלא נמצאו בתיק לקוח. עבור כל פוליסה
          ניתן להוסיף נכס ידני חדש.
        </Alert>

        {insurancePolicies.map((policy, index) => {
          const contact = getContactById(policy.contact_id);
          if (!contact) return null;

          return (
            <Accordion
              key={`${policy.id}-${index}`}
              expanded={expandedPanel === `panel-${index}`}
              onChange={handleAccordionChange(`panel-${index}`)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    פוליסה {policy.policy_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contact.first_name} {contact.last_name} • {formatHandlerCodeUniversal(policy.handler_code)}
                   
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box sx={{ width: "100%" }}>
                  {/* טופס הוספת נכס ידני */}
                  <ManualAssetForm
                    contacts={[contact]}
                    productType={ManualProductType.PURE_SAVINGS_POLICY}
                    reportedPeriod={reportedPeriod}
                    initialHandlerCode={policy.handler_code}
                    initialPolicyNumber={policy.policy_number}
                    initialFundsSum={policy.coverage_sum || undefined}
                    onSave={() => {
                      setExpandedPanel(false);
                      onPolicySaved?.(policy);
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {insurancePolicies.length === 0 && (
          <Alert severity="warning">לא נמצאו פוליסות חיסכון להמרה</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          סגור
        </Button>
      </DialogActions>
    </Dialog>
  );
};
